import { useRef, useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Play, Pause } from "lucide-react";
import { VIDEOS } from "../data";

const VideoCard = ({ v, index }) => {
  const ref = useRef(null);
  const wrapRef = useRef(null);
  const [playing, setPlaying] = useState(true);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const el = wrapRef.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { rootMargin: "300px" }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  const toggle = () => {
    const el = ref.current;
    if (!el) return;
    if (el.paused) {
      el.play();
      setPlaying(true);
    } else {
      el.pause();
      setPlaying(false);
    }
  };

  return (
    <motion.div
      ref={wrapRef}
      initial={{ opacity: 0, y: 60 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-80px" }}
      transition={{ duration: 0.8, delay: index * 0.1, ease: [0.22, 1, 0.36, 1] }}
      data-testid={`video-card-${index}`}
      className={`group relative overflow-hidden rounded-2xl border border-[#FCFBF8]/10 ${
        index === 0 ? "md:col-span-6 md:row-span-2 h-[360px] md:h-[520px]" : "md:col-span-6 h-[250px]"
      }`}
    >
      <video
        ref={ref}
        src={inView ? v.src : undefined}
        poster={v.poster}
        autoPlay
        muted
        loop
        playsInline
        preload="none"
        className="h-full w-full object-cover transition-transform duration-[900ms] group-hover:scale-105"
      />
      <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/10 to-transparent" />

      <div className="absolute inset-x-0 bottom-0 flex items-end justify-between p-6">
        <div>
          <span className="font-body text-[10px] uppercase tracking-[0.3em] text-[#EAD39C]">
            {v.tag}
          </span>
          <p className="mt-1 font-heading text-2xl font-light text-[#FCFBF8]">{v.title}</p>
        </div>
        <button
          onClick={toggle}
          data-testid={`video-toggle-${index}`}
          aria-label={playing ? "Pause video" : "Play video"}
          className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#FCFBF8]/30 bg-black/30 text-[#FCFBF8] backdrop-blur-md transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#0D0C0A]"
        >
          {playing ? <Pause size={16} /> : <Play size={16} />}
        </button>
      </div>
    </motion.div>
  );
};

export const VideoShowcase = () => {
  return (
    <section id="film" className="relative overflow-hidden bg-[#0D0C0A] py-24 text-[#FCFBF8] sm:py-32 lg:py-40">
      <div className="mx-auto max-w-[1500px] px-6 sm:px-12 lg:px-16">
        <div className="mb-14 grid grid-cols-1 items-end gap-8 lg:grid-cols-12">
          <div className="lg:col-span-8">
            <div className="mb-6 flex items-center gap-4">
              <span className="h-px w-14 bg-[#C5A059]" />
              <span className="font-body text-xs font-semibold uppercase tracking-[0.3em] text-[#C5A059]">
                In Motion
              </span>
            </div>
            <h2 className="font-heading text-4xl font-light leading-[1.05] tracking-tight text-[#FCFBF8] sm:text-5xl lg:text-6xl">
              Celebrations that <span className="italic text-gradient-gold">move</span> you.
            </h2>
          </div>
          <p className="font-body text-base leading-relaxed text-[#FCFBF8]/60 lg:col-span-4">
            A glimpse of the atmosphere we create — candlelight, colour and choreography in motion.
          </p>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-12 lg:gap-6">
          {VIDEOS.map((v, i) => (
            <VideoCard key={i} v={v} index={i} />
          ))}
        </div>
      </div>
    </section>
  );
};
